import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import api from './utils/api';
import './App.css';

// Components
import { LoginPage } from './components/LoginPage';
import { AuthCallback } from './components/AuthCallback';
import { Dashboard } from './components/Dashboard';
import { HistoricalData } from './components/HistoricalData';
import { EmployeeActivity } from './components/EmployeeActivity';
import { BookingPipeline } from './components/BookingPipeline';
import { MarketingChannels } from './components/MarketingChannels';
import MarketingAnalyse from './pages/MarketingAnalyse';
import { Omraaderentabilitet } from './components/Omraaderentabilitet';
import { Kundeservice } from './components/Kundeservice';
import { Ferieregnsskab } from './components/Ferieregnsskab';
import { Konsulenter } from './components/Konsulenter';
import { KPIer } from './components/KPIer';
import SpendAnalyse from './pages/SpendAnalyse';
import Kapacitetsanalyse from './pages/Kapacitetsanalyse';
import Prognose from './pages/Prognose';
import F1Race from './pages/F1Race';
import F1Public from './pages/F1Public';
import Saldobalance from './pages/Saldobalance';
import OkonomiskAnalyse from './pages/OkonomiskAnalyse';
import MaanedsAnalyse from './pages/MaanedsAnalyse';
import Montor from './pages/Montor';
import MoederMarketing from './pages/MoederMarketing';
import MoederLedelsen from './pages/MoederLedelsen';
import DagsordenForslag from './pages/DagsordenForslag';
import KonsulentTimer from './pages/KonsulentTimer';
import Budget from './pages/Budget';
import BudgetCockpit from './pages/BudgetCockpit';
import Fastloennede from './pages/Fastloennede';
import Admin from './pages/Admin';
import Konkurrenter from './pages/Konkurrenter';
import UdgiftsAnalyse from './pages/UdgiftsAnalyse';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) return;
    
    if (location.state?.user) {
      const u = location.state.user;
      if (!u.is_dashboard_user && !u.is_admin && (!u.allowed_pages || u.allowed_pages.length === 0)) {
        setIsAuthenticated(false);
        localStorage.removeItem('session_token');
        navigate('/login', { state: { error: 'Du har ikke adgang til dashboardet. Kontakt din administrator.' } });
        return;
      }
      setIsAuthenticated(true);
      setUser(u);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await api.get('/auth/me');
        const u = response.data;
        if (!u.is_dashboard_user && !u.is_admin && (!u.allowed_pages || u.allowed_pages.length === 0)) {
          setIsAuthenticated(false);
          localStorage.removeItem('session_token');
          navigate('/login', { state: { error: 'Du har ikke adgang til dashboardet. Kontakt din administrator.' } });
          return;
        }
        setUser(u);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
        localStorage.removeItem('session_token');
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#66FCF1] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#C5C6C7] font-mono text-sm">AUTHENTICATING...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return React.cloneElement(children, { user });
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL fragment SYNCHRONOUSLY during render
  // Use window.location.hash directly for reliability
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (window.location.hash?.includes('session_id=') || location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/historik"
        element={
          <ProtectedRoute>
            <HistoricalData />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline"
        element={
          <ProtectedRoute>
            <BookingPipeline />
          </ProtectedRoute>
        }
      />
      <Route
        path="/medarbejder"
        element={
          <ProtectedRoute>
            <EmployeeActivity />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing"
        element={
          <ProtectedRoute>
            <MarketingChannels />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing-analyse"
        element={
          <ProtectedRoute>
            <MarketingAnalyse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/omraaderentabilitet"
        element={
          <ProtectedRoute>
            <Omraaderentabilitet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/konkurrenter"
        element={
          <ProtectedRoute>
            <Konkurrenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kundeservice"
        element={
          <ProtectedRoute>
            <Kundeservice />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ferieregnsskab"
        element={
          <ProtectedRoute>
            <Ferieregnsskab />
          </ProtectedRoute>
        }
      />
      <Route
        path="/konsulenter"
        element={
          <ProtectedRoute>
            <Konsulenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kpier"
        element={
          <ProtectedRoute>
            <KPIer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spend-analyse"
        element={
          <ProtectedRoute>
            <SpendAnalyse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kapacitetsanalyse"
        element={
          <ProtectedRoute>
            <Kapacitetsanalyse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prognose"
        element={
          <ProtectedRoute>
            <Prognose />
          </ProtectedRoute>
        }
      />
      <Route
        path="/f1"
        element={
          <ProtectedRoute>
            <F1Race />
          </ProtectedRoute>
        }
      />
      <Route
        path="/saldobalance"
        element={
          <ProtectedRoute>
            <Saldobalance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/okonomisk-analyse"
        element={
          <ProtectedRoute>
            <OkonomiskAnalyse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maaneds-analyse"
        element={
          <ProtectedRoute>
            <MaanedsAnalyse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/montoerer"
        element={
          <ProtectedRoute>
            <Montor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/f1/live/:token"
        element={<F1Public />}
      />
      <Route
        path="/f1/konkurrence"
        element={<F1Race />}
      />
      <Route
        path="/moeder/marketing"
        element={
          <ProtectedRoute>
            <MoederLedelsen meetingType="marketing" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/moeder/system"
        element={
          <ProtectedRoute>
            <MoederLedelsen meetingType="system" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/moeder/kundeservice"
        element={
          <ProtectedRoute>
            <MoederLedelsen meetingType="kundeservice" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/moeder/produkt"
        element={
          <ProtectedRoute>
            <MoederLedelsen meetingType="produkt" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/moeder/ledelsen"
        element={
          <ProtectedRoute>
            <MoederLedelsen meetingType="ledelsen" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dagsorden-forslag/:meetingId"
        element={<DagsordenForslag />}
      />
      <Route
        path="/dagsorden-forslag-marketing/:meetingId"
        element={<DagsordenForslag meetingType="marketing" />}
      />
      <Route
        path="/dagsorden-forslag-system/:meetingId"
        element={<DagsordenForslag meetingType="system" />}
      />
      <Route
        path="/dagsorden-forslag-kundeservice/:meetingId"
        element={<DagsordenForslag meetingType="kundeservice" />}
      />
      <Route
        path="/dagsorden-forslag-produkt/:meetingId"
        element={<DagsordenForslag meetingType="produkt" />}
      />
      <Route
        path="/konsulenttimer"
        element={
          <ProtectedRoute>
            <KonsulentTimer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/budget"
        element={
          <ProtectedRoute>
            <Budget />
          </ProtectedRoute>
        }
      />
      <Route
        path="/budget-cockpit"
        element={
          <ProtectedRoute>
            <BudgetCockpit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/udgifts-analyse"
        element={
          <ProtectedRoute>
            <UdgiftsAnalyse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fastloennede"
        element={
          <ProtectedRoute>
            <Fastloennede />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
